import { celestialObjects } from "./celestial.js";
import { cameraDirectionFromOrientation, HeadingTracker } from "./orientation.js";
import { isPassActive, isPassImminent, isSatelliteSunlit, nextPassForSatrec, normalizeDegrees, passVisibility, satrecFromOrbit, shortestAngle, sunElevation, lookAnglesForSatrec } from "./orbit.js";
import { satelliteInfoUrl } from "./satellite-info.js";
import { isTrackerObject, trackerObjectLabel } from "./tracker-objects.js";

const el = Object.fromEntries([...document.querySelectorAll("[id]")].map((node) => [node.id, node]));
const state = {
  latitude: 60.1699,
  longitude: 24.9384,
  altitude: 25,
  heading: null,
  elevation: null,
  cameraStream: null,
  object: null,
  satrec: null,
  pass: null,
  visibility: null,
  autoSelected: false,
};

let config = null;
let objects = new Map();
let launches = [];
let upcoming = [];
let planetCache = { stamp: 0, objects: [] };
let detailsItem = null;
const headingTracker = new HeadingTracker();

const compassWarnings = {
  poor: "Kompassi epätarkka · siirry kauemmas metallista ja kalibroi puhelin.",
  unstable: "Kompassisuunta vaihtelee · siirry kauemmas metallista.",
  relative: "Pohjoissuuntaa ei voitu varmistaa.",
};

const notificationModeNotes = {
  highlights: "Suositus: ISS, tuoreet Starlink-junat ja muut erityisen hyvät havaintomahdollisuudet. Enintään yksi ilmoitus 12 tunnissa.",
  all: "Kaikki valitut laskennallisesti havaittavat ohilennot ilman uuden laukaisun ennakkoilmoituksia.",
  custom: "Valitse kohteet, minimikorkeus sekä ohilento- ja laukaisuilmoitukset itse.",
};

const manualPlaces = [
  { id: "helsinki", name: "Helsinki", latitude: 60.1699, longitude: 24.9384 },
  { id: "turku", name: "Turku", latitude: 60.4518, longitude: 22.2666 },
  { id: "tampere", name: "Tampere", latitude: 61.4978, longitude: 23.7610 },
  { id: "jyvaskyla", name: "Jyväskylä", latitude: 62.2426, longitude: 25.7473 },
  { id: "kuopio", name: "Kuopio", latitude: 62.8924, longitude: 27.6770 },
  { id: "oulu", name: "Oulu", latitude: 65.0121, longitude: 25.4651 },
  { id: "rovaniemi", name: "Rovaniemi", latitude: 66.5039, longitude: 25.7294 },
];

const formatTime = new Intl.DateTimeFormat("fi-FI", {
  timeZone: "Europe/Helsinki", weekday: "short", hour: "2-digit", minute: "2-digit",
});
const formatDetailTime = new Intl.DateTimeFormat("fi-FI", {
  timeZone: "Europe/Helsinki", weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit",
});

function toast(message, timeout = 5000) {
  el.toast.textContent = message;
  el.toast.hidden = false;
  clearTimeout(toast.timer);
  toast.timer = setTimeout(() => { el.toast.hidden = true; }, timeout);
}

function cachedPlanets(now) {
  if (now.getTime() - planetCache.stamp > 5000) {
    planetCache = {
      stamp: now.getTime(),
      objects: celestialObjects(now, state.latitude, state.longitude, state.altitude),
    };
  }
  return planetCache.objects;
}

function addHud() {
  if (state.heading === null || state.elevation === null) return;
  const horizonY = 50 + (state.elevation / 34) * 50;
  if (horizonY >= -5 && horizonY <= 105) {
    const line = document.createElement("span");
    line.className = "horizon-line";
    line.style.top = `${horizonY}%`;
    line.textContent = "0°";
    el.skyObjects.append(line);
  }
  for (const [azimuth, label] of [[0, "P"], [90, "I"], [180, "E"], [270, "L"]]) {
    const error = shortestAngle(azimuth, state.heading);
    if (Math.abs(error) > 45) continue;
    const marker = document.createElement("span");
    marker.className = "cardinal";
    marker.textContent = label;
    marker.style.left = `${50 + (error / 45) * 50}%`;
    el.skyObjects.append(marker);
  }
}

function addSkyMarker(object) {
  if (object.elevation < -5 || state.heading === null || state.elevation === null) return false;
  const xError = shortestAngle(object.azimuth, state.heading);
  const yError = object.elevation - state.elevation;
  if (Math.abs(xError) > 38 || Math.abs(yError) > 34) return false;
  const marker = document.createElement("span");
  marker.className = "sky-object";
  marker.dataset.kind = object.kind;
  marker.textContent = object.name;
  marker.style.left = `${50 + (xError / 38) * 50}%`;
  marker.style.top = `${50 - (yError / 34) * 50}%`;
  el.skyObjects.append(marker);
  return true;
}

function renderDirectionCue(target, visible, active) {
  if (!active || !target || state.heading === null || state.elevation === null || visible) {
    el.directionCue.hidden = true;
    return;
  }
  const horizontal = shortestAngle(target.azimuth, state.heading);
  const vertical = target.elevation - state.elevation;
  const dx = horizontal / 38;
  const dy = -vertical / 34;
  const scale = Math.max(1, Math.abs(dx), Math.abs(dy));
  el.directionCue.style.left = `${50 + (dx / scale) * 40}%`;
  el.directionCue.style.top = `${50 + (dy / scale) * 38}%`;
  el.directionArrow.style.transform = `rotate(${Math.atan2(dy, dx) * 180 / Math.PI + 90}deg)`;
  const turns = [];
  if (Math.abs(horizontal) >= 2) turns.push(`${Math.abs(Math.round(horizontal))}° ${horizontal < 0 ? "vasemmalle" : "oikealle"}`);
  if (Math.abs(vertical) >= 2) turns.push(`${Math.abs(Math.round(vertical))}° ${vertical < 0 ? "alas" : "ylös"}`);
  el.directionText.textContent = turns.join(" · ") || "Kohdalla";
  el.directionCue.hidden = false;
}

function visibilityReason(reason) {
  return reason === "earth-shadow" ? "Maan varjossa" : "päivänvalo";
}

function updateTargetStatus(now, target, active, observableNow) {
  if (!state.pass || !state.object) return;
  const launch = launchLabel(state.object);
  el.targetChip.classList.toggle("visible", observableNow);
  el.targetChip.classList.toggle("waiting", !observableNow);
  if (active && observableNow) {
    el.targetName.textContent = `Nyt havaittavissa · ${state.object.name}`;
    el.targetPass.textContent = `Havainto mahdollinen · ${Math.round(target.elevation)}° · ${launch}`;
  } else if (active) {
    el.targetName.textContent = `Horisontin yläpuolella · ${state.object.name}`;
    const reason = sunElevation(now, state.latitude, state.longitude, state.altitude) > -6 ? "daylight" : "earth-shadow";
    el.targetPass.textContent = `Ei paljain silmin · ${visibilityReason(reason)} · ${Math.round(target.elevation)}°`;
  } else if (now < state.pass.rise) {
    const minutes = Math.max(1, Math.ceil((state.pass.rise.getTime() - now.getTime()) / 60_000));
    el.targetName.textContent = `${state.autoSelected ? "Tulossa" : "Seurannassa"} · ${state.object.name}`;
    const visibility = state.visibility?.observable ? "havainto mahdollinen ylityksen aikana" : `ei paljain silmin · ${visibilityReason(state.visibility?.reason)}`;
    el.targetPass.textContent = `Horisontin yläpuolella ${minutes} min päästä · ${visibility}`;
  } else if (now > state.pass.set) {
    el.targetName.textContent = `Seurannassa · ${state.object.name}`;
    el.targetPass.textContent = "Ylitys päättynyt · päivitetään seuraavaa";
  } else {
    el.targetName.textContent = `Seurannassa · ${state.object.name}`;
    el.targetPass.textContent = `Ei juuri nyt näkyvissä · max ${Math.round(state.pass.peak.elevation)}°`;
  }
}

function renderHud() {
  el.heading.textContent = state.heading === null ? "–°" : `${Math.round(state.heading)}°`;
  el.tilt.textContent = state.elevation === null ? "–°" : `${Math.round(state.elevation)}°`;
  el.skyObjects.replaceChildren();
  addHud();
  const now = new Date();
  let target = null;
  let targetVisible = false;
  let targetActive = false;
  if (state.satrec && state.object) {
    try {
      target = lookAnglesForSatrec(state.satrec, now, state.latitude, state.longitude, state.altitude);
      targetActive = isPassActive(state.pass, now, target.elevation);
      const observableNow = targetActive
        && sunElevation(now, state.latitude, state.longitude, state.altitude) <= -6
        && isSatelliteSunlit(state.satrec, now);
      const trackableNow = targetActive && (!el.observableOnly.checked || observableNow);
      if (trackableNow) targetVisible = addSkyMarker({ name: state.object.name, kind: "satellite", ...target });
      updateTargetStatus(now, target, targetActive, observableNow);
      targetActive = trackableNow;
    } catch { /* stale orbit; list refresh will eventually remove it */ }
  }
  renderDirectionCue(target, targetVisible, targetActive);
  for (const planet of cachedPlanets(now)) addSkyMarker(planet);
}

function calculateUpcoming() {
  const now = new Date();
  const requested = new URLSearchParams(location.search).get("object");
  const previous = state.object ? String(state.object.id) : "";
  upcoming = [];
  for (const object of objects.values()) {
    if (!isTrackerObject(object, requested)) continue;
    const launch = object.launchId ? launches.find((item) => item.id === object.launchId) : null;
    if (launch && Date.parse(launch.net) > now.getTime()) continue;
    try {
      const satrec = satrecFromOrbit(object.orbit);
      const pass = nextPassForSatrec(satrec, now, state.latitude, state.longitude, 24, 60_000);
      if (pass && pass.peak.elevation >= 5) {
        const visibility = passVisibility(satrec, pass, state.latitude, state.longitude, state.altitude);
        upcoming.push({ object, satrec, pass, visibility });
      }
    } catch { /* invalid objects stay out of the UI */ }
  }
  upcoming.sort((a, b) => a.pass.rise - b.pass.rise);
  renderPassList();
  const ids = new Set(upcoming.map((item) => String(item.object.id)));
  const requestedItem = ids.has(requested) ? upcoming.find((item) => String(item.object.id) === requested) : null;
  const previousItem = ids.has(previous) ? upcoming.find((item) => String(item.object.id) === previous) : null;
  const automaticItem = upcoming.find((item) => isPassImminent(item.pass, now)
    && (!el.observableOnly.checked || item.visibility.observable));
  const selected = requestedItem || (!state.autoSelected && previousItem) || automaticItem;
  if (selected) selectObject(selected, false, selected === automaticItem && !requestedItem && (state.autoSelected || !previousItem));
  else clearObject();
}

function launchLabel(object) {
  return trackerObjectLabel(object);
}

function renderPassList() {
  el.passList.replaceChildren();
  const candidates = el.observableOnly.checked
    ? upcoming.filter((item) => item.visibility.observable)
    : upcoming;
  if (candidates.length === 0) {
    const empty = document.createElement("p");
    empty.textContent = el.observableOnly.checked
      ? "Ei laskennallisesti havaittavia ylityksiä seuraavan 24 h aikana."
      : "Ei ylityksiä seuraavan 24 h aikana.";
    el.passList.append(empty);
    return;
  }
  const groups = new Map();
  for (const item of candidates) {
    const key = item.object.launchKey || `object-${item.object.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(item);
  }
  const rows = [...groups.values()]
    .map((items) => ({ item: items[0], count: items.length }))
    .sort((a, b) => a.item.pass.rise - b.item.pass.rise);
  const elevationLimit = Number(el.minElevation.value || 20);
  for (const { item, count } of rows.slice(0, 100)) {
    const card = document.createElement("button");
    card.className = "pass-card";
    if (item.pass.peak.elevation < elevationLimit) card.classList.add("below-limit");
    if (!item.visibility.observable) card.classList.add("not-observable");
    card.type = "button";
    const title = document.createElement("b");
    title.textContent = launchLabel(item.object);
    const detail = document.createElement("span");
    const countText = count > 1 ? ` · ${count} kohdetta` : "";
    const visibilityText = item.visibility.observable
      ? "havainto mahdollinen"
      : `ei paljain silmin · ${visibilityReason(item.visibility.reason)}`;
    detail.textContent = `${formatTime.format(item.pass.rise)} · ${Math.round(item.pass.peak.elevation)}° · ${visibilityText} · ${item.object.name}${countText}`;
    card.append(title, detail);
    card.addEventListener("click", () => openPassDetails(item, count));
    el.passList.append(card);
  }
}

function skyMapPoint(azimuth, elevation) {
  const radius = (90 - Math.max(0, Math.min(90, elevation))) / 90 * 145;
  const angle = azimuth * Math.PI / 180;
  return { x: 160 + Math.sin(angle) * radius, y: 160 - Math.cos(angle) * radius };
}

function openPassDetails(item, count) {
  detailsItem = item;
  el.detailsLaunch.textContent = launchLabel(item.object);
  el.detailsObject.textContent = count > 1 ? `${item.object.name} · ${count} kohdetta laukaisussa` : item.object.name;
  el.detailsInfo.href = satelliteInfoUrl(item.object);
  el.detailsInfo.setAttribute("aria-label", `Etsi tietoa kohteesta ${item.object.name} DuckDuckGolla`);
  const duration = item.pass.set.getTime() - item.pass.rise.getTime();
  const samples = [];
  for (let index = 0; index <= 40; index += 1) {
    const time = new Date(item.pass.rise.getTime() + duration * index / 40);
    const look = lookAnglesForSatrec(item.satrec, time, state.latitude, state.longitude, state.altitude);
    const point = skyMapPoint(look.azimuth, look.elevation);
    samples.push(`${point.x.toFixed(1)},${point.y.toFixed(1)}`);
  }
  el.detailsTrack.setAttribute("points", samples.join(" "));
  const peakLook = lookAnglesForSatrec(item.satrec, item.pass.peak.time, state.latitude, state.longitude, state.altitude);
  const peakPoint = skyMapPoint(peakLook.azimuth, peakLook.elevation);
  el.detailsPeakPoint.setAttribute("cx", peakPoint.x);
  el.detailsPeakPoint.setAttribute("cy", peakPoint.y);
  el.detailsPeakLabel.setAttribute("x", Math.min(274, peakPoint.x + 8));
  el.detailsPeakLabel.setAttribute("y", Math.max(18, peakPoint.y - 8));
  el.detailsPeakLabel.textContent = `${Math.round(item.pass.peak.elevation)}°`;
  el.detailsRise.textContent = formatDetailTime.format(item.pass.rise);
  el.detailsPeak.textContent = formatDetailTime.format(item.pass.peak.time);
  el.detailsSet.textContent = formatDetailTime.format(item.pass.set);
  const now = new Date();
  const currentLook = lookAnglesForSatrec(item.satrec, now, state.latitude, state.longitude, state.altitude);
  const active = isPassActive(item.pass, now, currentLook.elevation);
  const waitMinutes = Math.max(1, Math.ceil((item.pass.rise.getTime() - now.getTime()) / 60_000));
  const observableNow = active
    && sunElevation(now, state.latitude, state.longitude, state.altitude) <= -6
    && isSatelliteSunlit(item.satrec, now);
  if (observableNow) {
    el.detailsStatus.textContent = `Havainto mahdollinen nyt · ${Math.round(currentLook.elevation)}° korkeudella.`;
  } else if (active) {
    const reason = sunElevation(now, state.latitude, state.longitude, state.altitude) > -6 ? "daylight" : "earth-shadow";
    el.detailsStatus.textContent = `Horisontin yläpuolella, mutta ei paljain silmin · ${visibilityReason(reason)}.`;
  } else if (item.visibility.observable) {
    el.detailsStatus.textContent = `Horisontin yläpuolella ${waitMinutes} min päästä · havainto mahdollinen ylityksen aikana.`;
  } else {
    el.detailsStatus.textContent = `Horisontin yläpuolella ${waitMinutes} min päästä · ei paljain silmin · ${visibilityReason(item.visibility.reason)}.`;
  }
  el.showTarget.textContent = active ? "Näytä kamerassa" : "Seuraa tätä ylitystä";
  el.passesDialog.close();
  el.passDetailsDialog.showModal();
}

function selectObject(item, updateUrl = true, autoSelected = false) {
  state.object = item.object;
  state.satrec = item.satrec;
  state.pass = item.pass;
  state.visibility = item.visibility;
  state.autoSelected = autoSelected;
  el.targetChip.hidden = false;
  if (updateUrl) {
    const url = new URL(location.href);
    url.searchParams.set("object", item.object.id);
    history.replaceState(null, "", url);
  }
  renderHud();
}

function clearObject() {
  state.object = null;
  state.satrec = null;
  state.pass = null;
  state.visibility = null;
  state.autoSelected = false;
  el.targetChip.hidden = true;
  renderHud();
}

async function updateData() {
  try {
    const response = await fetch("/api/state", { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    launches = data.launches || [];
    objects = new Map((data.objects || []).map((object) => [String(object.id), object]));
    calculateUpcoming();
  } catch (error) {
    toast(`Data: ${error.message}`);
  }
}

function handleOrientation(event) {
  const direction = cameraDirectionFromOrientation(event);
  if (!direction) return;
  const tracked = headingTracker.update(direction, performance.now());
  if (Number.isFinite(tracked.heading)) state.heading = normalizeDegrees(tracked.heading);
  state.elevation = Math.max(-90, Math.min(90, direction.elevation));
  if (el.compassWarning.dataset.quality !== (tracked.warning || "")) {
    el.compassWarning.dataset.quality = tracked.warning || "";
    el.compassWarning.textContent = compassWarnings[tracked.warning] || "";
    el.compassWarning.hidden = !tracked.warning;
  }
  renderHud();
}

async function requestOrientation() {
  if (typeof DeviceOrientationEvent === "undefined") throw new Error("Asentoanturia ei ole.");
  if (typeof DeviceOrientationEvent.requestPermission === "function") {
    if (await DeviceOrientationEvent.requestPermission() !== "granted") throw new Error("Asentolupaa ei annettu.");
  }
  window.addEventListener("deviceorientation", handleOrientation, true);
}

async function requestCamera() {
  if (!navigator.mediaDevices?.getUserMedia) throw new Error("Kameraa ei ole.");
  if (state.cameraStream) return;
  state.cameraStream = await navigator.mediaDevices.getUserMedia({
    audio: false,
    video: { facingMode: { ideal: "environment" }, width: { ideal: 1920 }, height: { ideal: 1080 } },
  });
  el.camera.srcObject = state.cameraStream;
}

function requestLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      const error = new Error("GPS ei ole käytettävissä.");
      enableManualLocation(error.message);
      reject(error);
      return;
    }
    navigator.geolocation.getCurrentPosition(({ coords }) => {
      state.latitude = coords.latitude;
      state.longitude = coords.longitude;
      state.altitude = coords.altitude || 0;
      el.gpsStatus.textContent = "GPS käytössä";
      el.manualLocation.hidden = true;
      calculateUpcoming();
      resolve();
    }, (failure) => {
      const error = new Error(failure.message || "GPS-sijaintia ei saatu.");
      enableManualLocation(error.message);
      reject(error);
    }, {
      enableHighAccuracy: true, timeout: 12_000, maximumAge: 30_000,
    });
  });
}

function applyManualLocation() {
  const place = manualPlaces.find((item) => item.id === el.manualPlace.value) || manualPlaces[0];
  state.latitude = place.latitude;
  state.longitude = place.longitude;
  state.altitude = 0;
  el.gpsStatus.textContent = `GPS ei käytössä · ${place.name}`;
  try { localStorage.setItem("manual-place", place.id); } catch { /* Private mode can deny storage. */ }
  calculateUpcoming();
}

function enableManualLocation(message) {
  el.manualLocation.hidden = false;
  el.gpsStatus.textContent = `GPS ei käytettävissä: ${message}`;
  let saved = "helsinki";
  try { saved = localStorage.getItem("manual-place") || saved; } catch { /* Private mode can deny storage. */ }
  if (manualPlaces.some((place) => place.id === saved)) el.manualPlace.value = saved;
  applyManualLocation();
}

async function startSensors() {
  el.start.disabled = true;
  const results = await Promise.allSettled([requestOrientation(), requestCamera(), requestLocation()]);
  const errors = results.filter((item) => item.status === "rejected").map((item) => item.reason.message);
  if (errors.length) toast(errors.join(" "), 8000);
  if (state.cameraStream || state.heading !== null) el.start.hidden = true;
  el.start.disabled = false;
}

function base64Key(value) {
  const padding = "=".repeat((4 - value.length % 4) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  return Uint8Array.from(atob(base64), (character) => character.charCodeAt(0));
}

function preferences() {
  return {
    notificationMode: el.notificationMode.value,
    region: el.region.value,
    minElevation: Number(el.minElevation.value),
    objectTypes: [...el.objectTypes.querySelectorAll("input:checked")].map((input) => input.value),
    timeWindows: [...el.timeWindows.querySelectorAll("input:checked")].map((input) => input.value),
    passAlerts: el.passAlerts.checked,
    launchAlerts: el.launchAlerts.checked,
    observableOnly: el.observableOnly.checked,
  };
}

async function enablePush() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) throw new Error("Web Push ei ole käytettävissä.");
  const registration = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  if (await Notification.requestPermission() !== "granted") throw new Error("Ilmoituslupaa ei annettu.");
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: base64Key(config.vapidPublicKey),
  });
  const response = await fetch("/api/subscriptions", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ subscription: subscription.toJSON(), preferences: preferences() }),
  });
  if (!response.ok) throw new Error((await response.json()).error || `HTTP ${response.status}`);
  el.enableNotifications.textContent = "Tallenna";
  el.disableNotifications.hidden = false;
  el.notificationStatus.textContent = "Käytössä";
}

async function disablePush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    await fetch("/api/subscriptions", {
      method: "DELETE", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ endpoint: subscription.endpoint }),
    });
    await subscription.unsubscribe();
  }
  el.enableNotifications.textContent = "Ilmoitukset päälle";
  el.disableNotifications.hidden = true;
  el.notificationStatus.textContent = "Pois käytöstä";
}

async function initializeSettings() {
  try {
    const response = await fetch("/api/config", { cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    config = await response.json();
    for (const mode of config.notificationModes) el.notificationMode.add(new Option(mode.name, mode.id));
    for (const region of config.regions) el.region.add(new Option(region.name, region.id));
    for (const limit of config.elevationLimits) el.minElevation.add(new Option(`${limit}°`, limit));
    for (const item of config.objectTypes) addCheckbox(el.objectTypes, item.id, item.name);
    for (const item of config.timeWindows) addCheckbox(el.timeWindows, item.id, item.name, ["evening", "night"].includes(item.id));
    el.notificationMode.value = "highlights";
    el.region.value = "south";
    el.minElevation.value = "20";
    updateNotificationMode();
    renderPassList();
    if ("serviceWorker" in navigator) {
      const registration = await navigator.serviceWorker.register("/sw.js");
      const subscription = await registration.pushManager?.getSubscription();
      if (subscription) {
        el.enableNotifications.textContent = "Tallenna";
        el.disableNotifications.hidden = false;
        el.notificationStatus.textContent = "Käytössä";
      }
    }
  } catch (error) {
    el.notificationStatus.textContent = error.message;
  }
}

function addCheckbox(container, value, labelText, checked = true) {
  const label = document.createElement("label");
  label.className = "check";
  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.value = value;
  checkbox.checked = checked;
  label.append(checkbox, labelText);
  container.append(label);
}

function updateNotificationMode() {
  const mode = el.notificationMode.value || "highlights";
  el.notificationModeNote.textContent = notificationModeNotes[mode] || "";
  el.detailedNotificationSettings.hidden = mode === "highlights";
  el.customNotificationSettings.hidden = mode !== "custom";
}

el.start.addEventListener("click", () => void startSensors());
el.useGps.addEventListener("click", () => requestLocation().catch((error) => toast(error.message)));
el.manualPlace.addEventListener("change", applyManualLocation);
el.openPasses.addEventListener("click", () => el.passesDialog.showModal());
el.openSettings.addEventListener("click", () => el.settingsDialog.showModal());
document.querySelectorAll("[data-close]").forEach((button) => button.addEventListener("click", () => el[button.dataset.close].close()));
el.showTarget.addEventListener("click", () => {
  if (!detailsItem) return;
  selectObject(detailsItem);
  el.passDetailsDialog.close();
});
el.enableNotifications.addEventListener("click", () => enablePush().catch((error) => { el.notificationStatus.textContent = error.message; }));
el.disableNotifications.addEventListener("click", () => disablePush().catch((error) => { el.notificationStatus.textContent = error.message; }));
el.minElevation.addEventListener("change", renderPassList);
el.observableOnly.addEventListener("change", calculateUpcoming);
el.notificationMode.addEventListener("change", updateNotificationMode);
el.introContinue.addEventListener("click", () => {
  try { localStorage.setItem("intro-seen", "1"); } catch { /* Private mode can deny storage. */ }
  el.introDialog.close();
  void startSensors();
});

function initializeIntro() {
  let seen = false;
  try { seen = localStorage.getItem("intro-seen") === "1"; } catch { /* Show intro when storage is unavailable. */ }
  if (seen) {
    void requestCamera().then(() => { el.start.textContent = "Kompassi"; }).catch(() => {});
  } else {
    el.introDialog.showModal();
  }
}

for (const place of manualPlaces) el.manualPlace.add(new Option(place.name, place.id));
void initializeSettings();
void updateData();
initializeIntro();
setInterval(renderHud, 1000);
setInterval(() => void updateData(), 5 * 60_000);
